import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class TokenService {
  private readonly ACCESS_TOKEN_KEY = 'access_token';
  private readonly REFRESH_TOKEN_KEY = 'refresh_token';
  private readonly REFRESH_BUFFER_SECONDS = 30; // Buffer de 30 segundos antes de la expiración

  private http = inject(HttpClient);
  private apiGatewayUrl = environment.apiGatewayUrl;
  private refreshTimer: any = null;

  // BehaviorSubject para notificar cuando el token se refresca
  private tokenRefreshed$ = new BehaviorSubject<boolean>(false);

  setTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem(this.ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);

    this.setupAutoRefresh(accessToken);
  }

  private setupAutoRefresh(token: string): void {
    // Limpiar timer anterior si existe
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp * 1000; // Convertir a millisegundos
      const now = Date.now();
      const timeUntilExpiry = exp - now;

      // Calcular tiempo para refresh (30 segundos antes de expirar)
      const refreshTime = timeUntilExpiry - this.REFRESH_BUFFER_SECONDS * 1000;

      // Solo programar si el tiempo es positivo
      if (refreshTime > 0) {
        this.refreshTimer = setTimeout(() => {
          this.performAutoRefresh();
        }, refreshTime);
      } else {
        console.warn('Token ya está cerca de expirar, refresh inmediato');
        this.performAutoRefresh();
      }
    } catch (error) {
      console.error('Error configurando auto-refresh:', error);
    }
  }

  private performAutoRefresh(): void {
    this.refreshToken().subscribe({
      next: (response) => {
        if (response.accessToken) {
          this.setTokens(
            response.accessToken,
            response.refreshToken || this.getRefreshToken()!
          );
          this.tokenRefreshed$.next(true);
        }
      },
      error: (error) => {
        console.error('Error en refresh automático:', error);
        // Aquí podrías emitir un evento para que el interceptor maneje el logout
      },
    });
  }

  getAccessToken(): string | null {
    return localStorage.getItem(this.ACCESS_TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  clearTokens(): void {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);

    // Limpiar timer de auto-refresh si existe
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  hasValidToken(): boolean {
    const token = this.getAccessToken();
    return !!token && !this.isTokenExpired(token);
  }

  isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp * 1000; // Convert to milliseconds
      return Date.now() > exp;
    } catch (error) {
      console.error('Error checking token expiration:', error);
      return true; // Consider expired if can't decode
    }
  }

  decodeToken(token: string): string {
    try {
      const payload = token.split('.')[1];
      const decodedPayload = atob(payload);

      return JSON.parse(decodedPayload).sub;
    } catch (error) {
      console.error('Error decoding token:', error);
      throw new Error('Invalid token format');
    }
  }

  refreshToken(): Observable<any> {
    const refreshToken = this.getRefreshToken();

    return this.http.post(`${this.apiGatewayUrl}/account/refresh-token`, {
      refreshToken: refreshToken,
    });
  }

  // Método para obtener notificaciones de refresh
  getTokenRefreshNotification(): Observable<boolean> {
    return this.tokenRefreshed$.asObservable();
  }

  // Método para inicializar el auto-refresh al cargar la app
  initializeAutoRefresh(): void {
    const token = this.getAccessToken();
    if (token && !this.isTokenExpired(token)) {
      this.setupAutoRefresh(token);
    }
  }
}
